import { useState, useCallback } from 'react'
import { Head } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import PageHeader from '@/components/ui/PageHeader'
import Notification from '@/components/ui/Notification'
import DataTable, { useFlash, usePagination, type DataTableColumn } from '@/components/ui/DataTable'
import { FormSelect, FormInput, FormTextarea } from '@/components/ui/form'
import FormMultiSelect from '@/components/ui/form/FormMultiSelect'
import Button from '@/components/ui/Button'
import Pagination from '@/components/ui/Pagination'
import { Badge } from '@/components/catalyst/badge'
import { Dialog, DialogTitle, DialogBody, DialogActions } from '@/components/catalyst/dialog'
import { PencilIcon, TrashIcon, CreditCardIcon } from '@heroicons/react/24/outline'

// ─── Types ──────────────────────────────────────

interface PaymentOptionRow {
  id: number
  name: string
  ein: string | null
  address: string | null
  form_of_payment_from_customer_list: string[]
  form_of_payment_to_vendor_list: string[]
}

interface Props {
  payment_options: PaymentOptionRow[]
  from_customer_tags: string[]
  to_vendor_tags: string[]
}

const PAGE_SIZES = [30, 50, 100, 200]

const emptyForm = {
  name: '', ein: '', address: '',
  form_of_payment_from_customer_list: [] as string[],
  form_of_payment_to_vendor_list: [] as string[],
}

// ─── Component ──────────────────────────────────

export default function PaymentOptionsIndex({ payment_options: initial, from_customer_tags, to_vendor_tags }: Props) {
  const [items, setItems] = useState(initial)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<PaymentOptionRow | null>(null)
  const [form, setForm] = useState({ ...emptyForm })
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)
  const { flashRow, flash } = useFlash()
  const csrfToken = typeof document !== 'undefined' ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '' : ''
  const notify = useCallback((msg: string) => { setNotifMessage(msg); setShowNotif(true) }, [])
  const pageSizeOptions = PAGE_SIZES.map(n => ({ value: String(n), label: `${n} per page` }))
  const { pageSize, setPageSize, currentPage, setCurrentPage, totalPages, paginatedRows, totalCount } = usePagination(items)

  const set = (key: string, val: string | string[]) => setForm(prev => ({ ...prev, [key]: val }))

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm }); setFormErrors([]); setDialogOpen(true) }
  const openEdit = (row: PaymentOptionRow) => {
    setEditing(row)
    setForm({
      name: row.name,
      ein: row.ein || '',
      address: row.address || '',
      form_of_payment_from_customer_list: row.form_of_payment_from_customer_list || [],
      form_of_payment_to_vendor_list: row.form_of_payment_to_vendor_list || [],
    })
    setFormErrors([])
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    const errors: string[] = []
    if (!form.name.trim()) errors.push('Name is required')
    if (!form.ein.trim()) errors.push('EIN is required')
    if (!form.address.trim()) errors.push('Address is required')
    if (errors.length) { setFormErrors(errors); return }
    setFormErrors([]); setSubmitting(true)
    try {
      const url = editing ? `/inertia_payment_options/${editing.id}.json` : '/inertia_payment_options.json'
      const method = editing ? 'PATCH' : 'POST'
      const payload = {
        company: {
          name: form.name.trim(),
          ein: form.ein.trim(),
          address: form.address.trim(),
          is_payment_option: true,
          form_of_payment_from_customer_list: form.form_of_payment_from_customer_list,
          form_of_payment_to_vendor_list: form.form_of_payment_to_vendor_list,
        },
      }
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }, body: JSON.stringify(payload) })
      if (res.ok) {
        const data = await res.json()
        if (editing) { setItems(prev => prev.map(r => r.id === data.id ? data : r)); flash(data.id, 'success') }
        else { setItems(prev => [data, ...prev]) }
        setDialogOpen(false); notify(editing ? 'Saved:Payment option updated' : 'Saved:Payment option created')
      } else { const err = await res.json().catch(() => ({})); setFormErrors(err.errors || ['Failed to save']) }
    } catch { setFormErrors(['Network error']) }
    setSubmitting(false)
  }

  const deleteItem = useCallback(async (id: number) => {
    if (!confirm('Delete this payment option?')) return
    try {
      const res = await fetch(`/inertia_payment_options/${id}.json`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } })
      if (res.ok) { setItems(prev => prev.filter(r => r.id !== id)); notify('Deleted:Payment option removed') }
    } catch { /* silent */ }
  }, [csrfToken, notify])

  const fromCustomerOptions = from_customer_tags.map(t => ({ value: t, label: t }))
  const toVendorOptions = to_vendor_tags.map(t => ({ value: t, label: t }))

  const columns: DataTableColumn<PaymentOptionRow>[] = [
    { key: 'name', label: 'Name', render: row => <span className="font-medium">{row.name}</span>, sortValue: row => row.name, filterValue: row => row.name },
    { key: 'ein', label: 'EIN', render: row => <>{row.ein || '—'}</>, sortValue: row => row.ein || '', filterValue: row => row.ein || '' },
    { key: 'address', label: 'Address', render: row => <span className="block max-w-[200px] truncate" title={row.address || ''}>{row.address || '—'}</span>, sortValue: row => row.address || '', filterValue: row => row.address || '' },
    { key: 'from_customer', label: 'From Customer', render: row => row.form_of_payment_from_customer_list.length > 0 ? <div className="flex flex-wrap gap-1">{row.form_of_payment_from_customer_list.map(t => <Badge key={t} color="sky">{t}</Badge>)}</div> : <span className="text-zinc-400">—</span>, noFilter: true },
    { key: 'to_vendor', label: 'To Vendor', render: row => row.form_of_payment_to_vendor_list.length > 0 ? <div className="flex flex-wrap gap-1">{row.form_of_payment_to_vendor_list.map(t => <Badge key={t} color="emerald">{t}</Badge>)}</div> : <span className="text-zinc-400">—</span>, noFilter: true },
  ]

  return (
    <>
      <Head title="Payment Options" />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />
      <PageHeader title="Payment Options" icon={<CreditCardIcon className="h-4 w-4" />} actions={
        <div className="flex items-center gap-2">
          <div className="w-36"><FormSelect value={String(pageSize)} onChange={v => { setPageSize(Number(v)); setCurrentPage(0) }} options={pageSizeOptions} /></div>
          <Button onClick={openCreate}>+ New Payment Option</Button>
        </div>
      } />
      <div className="mb-2"><Badge color="zinc"><span className="text-xs font-medium">{totalCount} payment options</span></Badge></div>

      <DataTable rows={paginatedRows} columns={columns} rowKey={row => row.id} flashRowId={flashRow?.id ?? null} flashStatus={flashRow?.status ?? null} emptyText="No payment options" filteredEmptyText="No payment options match your filters"
        actions={row => (
          <div className="flex items-center justify-end gap-1">
            <button onClick={() => openEdit(row)} className="rounded p-1 text-zinc-400 hover:text-indigo-600 dark:text-zinc-500 dark:hover:text-indigo-400" title="Edit"><PencilIcon className="h-4 w-4" /></button>
            <button onClick={() => deleteItem(row.id)} className="rounded p-1 text-zinc-400 hover:text-red-600 dark:text-zinc-500 dark:hover:text-red-400" title="Delete"><TrashIcon className="h-4 w-4" /></button>
          </div>
        )} />

      <Pagination currentPage={currentPage} totalPages={totalPages} pageSize={pageSize} totalCount={totalCount} onPageChange={setCurrentPage} />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{editing ? 'Edit Payment Option' : 'New Payment Option'}</DialogTitle>
        <DialogBody>
          {formErrors.length > 0 && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 dark:border-red-800 dark:bg-red-900/20">
              <ul className="list-disc pl-4 text-xs text-red-700 dark:text-red-400">{formErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Name *</label>
              <FormInput value={form.name} onChange={e => set('name', (e.target as HTMLInputElement).value)} placeholder="Payment option name..." autoFocus />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">EIN *</label>
              <FormInput value={form.ein} onChange={e => set('ein', (e.target as HTMLInputElement).value)} placeholder="EIN..." />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Address *</label>
              <FormTextarea value={form.address} onChange={e => set('address', (e.target as HTMLTextAreaElement).value)} rows={2} placeholder="Address..." />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Forms of Payment from Customer</label>
              <FormMultiSelect
                values={form.form_of_payment_from_customer_list}
                options={fromCustomerOptions}
                onChange={vals => set('form_of_payment_from_customer_list', vals as string[])}
                allowCreate
                placeholder="Select or type..."
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Forms of Payment to Vendor</label>
              <FormMultiSelect
                values={form.form_of_payment_to_vendor_list}
                options={toVendorOptions}
                onChange={vals => set('form_of_payment_to_vendor_list', vals as string[])}
                allowCreate
                placeholder="Select or type..."
              />
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button disabled={submitting} onClick={handleSubmit}>{submitting ? 'Saving...' : (editing ? 'Save' : 'Create')}</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

PaymentOptionsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
